import CalculateIcon from "@mui/icons-material/Calculate";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { Box, Chip, Divider, Stack, Typography, useTheme } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { SystemValidationTextField } from "../../../../components/UIToolkit/Inputs/SystemValidationTextField";
import SystemDialog from "../../../../components/UIToolkit/Popups/Dialog";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import { BADGE, FONT_SIZE } from "../../../../theme/tokens";
import type { PbjModelUpsertBody } from "../../../../utils/models";
import type { CasperCmi } from "../casperCmi.utils";
import { brandBorder, brandWash } from "../colors";
import { NATIONAL_DEFAULTS } from "../nationalDefaults";

type Props = {
	open: boolean;
	onClose: () => void;
	// The live config draft the fields read + write (edited via onDraftChange, live).
	draft: PbjModelUpsertBody;
	// Which CMI pair the scorer used, and whether it came from CASPER — the fields and the formula
	// display these so the dialog can't show a stale national default beside a CASPER-derived score.
	casperCmi: CasperCmi;
	// The computed CMI (pccPdpmAvg ÷ natNursingCaseMixIndex) shown in the formula box.
	cmi: number;
	// Keeps the parent's dirty draft in sync with a pending (not-yet-recomputed) edit.
	onDraftChange: (next: PbjModelUpsertBody) => void;
	// Persist + recompute the draft in one action (dialog Save).
	onCalculate: (next: PbjModelUpsertBody) => void;
};

/**
 * The hero's Facility Inputs (CMI) dialog: the facility CMI fraction, the national reported +
 * case-mix HRD snapshots, and the safety-margin buffers — with a reset to the national
 * defaults. Average census is deliberately NOT here; it has its own `CensusDialog` behind the
 * hero's Census chip, so each chip opens exactly the menu it names. Save persists + recomputes
 * via `onCalculate`. Lifted out of `PredictionHero` since it's one of the hero's actions
 * (mirrors the already-separate SetTargetDialog / ManualHoursButton pattern).
 */
export const FacilityInputsDialog: FC<Props> = ({ open, onClose, draft, cmi, casperCmi, onDraftChange, onCalculate }) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const accent = theme.palette.primary.main;

	const handleDraftField = (field: keyof PbjModelUpsertBody) => (e: React.ChangeEvent<HTMLInputElement>) => {
		const raw = Number.parseFloat(e.target.value);
		const value = Number.isFinite(raw) ? Math.max(0, raw) : 0;
		if (field === "pccPdpmAvg" || field === "natNursingCaseMixIndex") {
			// Hand-editing either CMI field pins the pair as manual ON THE DRAFT — not just in Save —
			// so the flag travels with the edit whichever path persists it (dialog Save or a page-level
			// Calculate after a plain Close). Otherwise the backend would overwrite it with CASPER again.
			// When CASPER values are on display, materialize BOTH into the draft first so the untouched
			// field can't snap back to the stale national default as `casperCmi.active` flips off.
			const casperBase = casperCmi.active ? { pccPdpmAvg: casperCmi.pccPdpmAvg, natNursingCaseMixIndex: casperCmi.natNursingCaseMixIndex } : {};
			onDraftChange({ ...draft, ...casperBase, [field]: value, cmiManual: true });
			return;
		}
		onDraftChange({ ...draft, [field]: value });
	};

	const handleBufferField = (field: "projectedScoreBufferPct" | "bandRangeBufferPct") => (e: React.ChangeEvent<HTMLInputElement>) => {
		const raw = Number.parseFloat(e.target.value);
		const max = field === "bandRangeBufferPct" ? 100 : Number.POSITIVE_INFINITY;
		const value = Number.isFinite(raw) ? Math.min(max, Math.max(0, raw)) : 0;
		onDraftChange({ ...draft, [field]: value });
	};

	return (
		<SystemDialog
			open={open}
			onClose={onClose}
			dataTestId="pbj-model-cmi-dialog"
			title={
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<CalculateIcon sx={{ fontSize: 20, color: accent }} />
					<Typography component="span" variant="h6" sx={{ fontWeight: 700 }}>
						{t("pbj_model.stars.cmi.dialog.title")}
					</Typography>
				</Box>
			}
			cancel={t("close")}
			confirm={t("save")}
			confirmButtonVariant="primary"
			onConfirm={() => {
				onCalculate(draft);
				onClose();
			}}
			body={
				<Stack spacing={2.5} sx={{ pt: 1 }}>
					<Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
						<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
							{t("pbj_model.config.section.facility_cmi")}
						</Typography>
						{/* Same badge treatment as the CASPER-sourced turnover measures in the breakdown.
						    Gated on `active` (not cmiSource alone) so it disappears the moment the fields
						    show hand-typed draft values instead of CASPER ones. */}
						{casperCmi.active && (
							<Chip
								label={t("pbj_model.scoring.casper.badge")}
								size="small"
								sx={{ height: BADGE.TAG_CHIP_HEIGHT_SM, fontSize: FONT_SIZE.LABEL_XXS, bgcolor: brandWash(theme), color: accent, fontWeight: 700 }}
								data-testid="pbj-model-cmi-casper-badge"
							/>
						)}
					</Stack>
					<Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
						<SystemValidationTextField
							label={t("pbj_model.config.pcc_pdpm_avg")}
							inputType="number"
							fullWidth
							reserveHeightForHelperText={false}
							value={String(casperCmi.pccPdpmAvg)}
							injectedProps={{ slotProps: { htmlInput: { min: 0, step: 0.01, "data-testid": "pbj-model-input-pcc_pdpm_avg" } } }}
							onChange={handleDraftField("pccPdpmAvg")}
							dataTestId="pbj-model-field-pcc_pdpm_avg"
						/>
						<SystemValidationTextField
							label={t("pbj_model.config.nat_nursing_cmi")}
							inputType="number"
							fullWidth
							reserveHeightForHelperText={false}
							value={String(casperCmi.natNursingCaseMixIndex)}
							injectedProps={{ slotProps: { htmlInput: { min: 0, step: 0.01, "data-testid": "pbj-model-input-nat_nursing_cmi" } } }}
							onChange={handleDraftField("natNursingCaseMixIndex")}
							dataTestId="pbj-model-field-nat_nursing_cmi"
						/>
					</Stack>
					<Box
						sx={{
							p: 2,
							bgcolor: brandWash(theme),
							borderRadius: 2,
							border: "1px dashed",
							borderColor: brandBorder(theme),
							textAlign: "center",
						}}
					>
						<Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1, fontWeight: 600 }}>
							{t("pbj_model.stars.cmi.dialog.formula")}
						</Typography>
						<Typography variant="body1">
							{casperCmi.pccPdpmAvg.toFixed(5)} ÷ {casperCmi.natNursingCaseMixIndex.toFixed(5)} ={" "}
							<Box component="strong" sx={{ color: accent, fontSize: "1.5rem" }}>
								{cmi.toFixed(2)}
							</Box>
						</Typography>
					</Box>

					<Divider />

					<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
						{t("pbj_model.config.section.nat_reported_hrds")}
					</Typography>
					<Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
						<SystemValidationTextField
							label={t("pbj_model.config.nat_reported_total_hrd")}
							inputType="number"
							fullWidth
							reserveHeightForHelperText={false}
							value={String(draft.natReportedTotalHrd)}
							injectedProps={{ slotProps: { htmlInput: { min: 0, step: 0.0001, "data-testid": "pbj-model-input-nat_reported_total_hrd" } } }}
							onChange={handleDraftField("natReportedTotalHrd")}
							dataTestId="pbj-model-field-nat_reported_total_hrd"
						/>
						<SystemValidationTextField
							label={t("pbj_model.config.nat_reported_rn_hrd")}
							inputType="number"
							fullWidth
							reserveHeightForHelperText={false}
							value={String(draft.natReportedRnHrd)}
							injectedProps={{ slotProps: { htmlInput: { min: 0, step: 0.0001, "data-testid": "pbj-model-input-nat_reported_rn_hrd" } } }}
							onChange={handleDraftField("natReportedRnHrd")}
							dataTestId="pbj-model-field-nat_reported_rn_hrd"
						/>
						<SystemValidationTextField
							label={t("pbj_model.config.nat_reported_weekend_hrd")}
							inputType="number"
							fullWidth
							reserveHeightForHelperText={false}
							value={String(draft.natReportedWeekendHrd)}
							injectedProps={{ slotProps: { htmlInput: { min: 0, step: 0.0001, "data-testid": "pbj-model-input-nat_reported_weekend_hrd" } } }}
							onChange={handleDraftField("natReportedWeekendHrd")}
							dataTestId="pbj-model-field-nat_reported_weekend_hrd"
						/>
					</Stack>

					<Divider />

					<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
						{t("pbj_model.config.section.nat_case_mix_hrds")}
					</Typography>
					<Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
						<SystemValidationTextField
							label={t("pbj_model.config.nat_case_mix_total_hrd")}
							inputType="number"
							fullWidth
							reserveHeightForHelperText={false}
							value={String(draft.natCaseMixTotalHrd)}
							injectedProps={{ slotProps: { htmlInput: { min: 0, step: 0.0001, "data-testid": "pbj-model-input-nat_case_mix_total_hrd" } } }}
							onChange={handleDraftField("natCaseMixTotalHrd")}
							dataTestId="pbj-model-field-nat_case_mix_total_hrd"
						/>
						<SystemValidationTextField
							label={t("pbj_model.config.nat_case_mix_rn_hrd")}
							inputType="number"
							fullWidth
							reserveHeightForHelperText={false}
							value={String(draft.natCaseMixRnHrd)}
							injectedProps={{ slotProps: { htmlInput: { min: 0, step: 0.0001, "data-testid": "pbj-model-input-nat_case_mix_rn_hrd" } } }}
							onChange={handleDraftField("natCaseMixRnHrd")}
							dataTestId="pbj-model-field-nat_case_mix_rn_hrd"
						/>
						<SystemValidationTextField
							label={t("pbj_model.config.nat_case_mix_weekend_hrd")}
							inputType="number"
							fullWidth
							reserveHeightForHelperText={false}
							value={String(draft.natCaseMixWeekendHrd)}
							injectedProps={{ slotProps: { htmlInput: { min: 0, step: 0.0001, "data-testid": "pbj-model-input-nat_case_mix_weekend_hrd" } } }}
							onChange={handleDraftField("natCaseMixWeekendHrd")}
							dataTestId="pbj-model-field-nat_case_mix_weekend_hrd"
						/>
					</Stack>

					<Divider />

					<Box>
						<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
							{t("pbj_model.config.section.safety_margin")}
						</Typography>
						<Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
							{t("pbj_model.config.safety_margin.helper")}
						</Typography>
					</Box>
					<Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ alignItems: "flex-start" }}>
						<SystemValidationTextField
							label={t("pbj_model.config.projected_score_buffer_points")}
							inputType="number"
							fullWidth
							value={String(draft.projectedScoreBufferPct)}
							defaultHelperText={t("pbj_model.config.projected_score_buffer_points.helper")}
							injectedProps={{ slotProps: { htmlInput: { min: 0, step: 1, "data-testid": "pbj-model-input-projected_score_buffer_points" } } }}
							onChange={handleBufferField("projectedScoreBufferPct")}
							dataTestId="pbj-model-field-projected_score_buffer_points"
						/>
						<SystemValidationTextField
							label={t("pbj_model.config.band_range_buffer_pct")}
							inputType="number"
							fullWidth
							maxValue={100}
							value={String(draft.bandRangeBufferPct)}
							defaultHelperText={t("pbj_model.config.band_range_buffer_pct.helper")}
							injectedProps={{ slotProps: { htmlInput: { min: 0, max: 100, step: 0.5, "data-testid": "pbj-model-input-band_range_buffer_pct" } } }}
							onChange={handleBufferField("bandRangeBufferPct")}
							dataTestId="pbj-model-field-band_range_buffer_pct"
						/>
					</Stack>

					{/* Reset defaults lives at the foot of the body (left-aligned, quiet) instead of
					    the fixed action row — it resets these national-default fields, so it reads as
					    a form-scoped tertiary action and keeps SystemDialog's row a clean Close/Save. */}
					<Divider />
					<Box>
						<SystemButton
							buttonVariant="borderless"
							size="small"
							leadingIcon={<RestartAltIcon sx={{ fontSize: 16 }} />}
							onClick={() => onDraftChange({ ...draft, ...NATIONAL_DEFAULTS, cmiManual: false })}
							noFullWidthMobile
							data-testid="pbj-model-reset-defaults"
						>
							{t("pbj_model.config.reset_defaults")}
						</SystemButton>
					</Box>
				</Stack>
			}
		/>
	);
};
